import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  scheduleDailyQuiz,
  updateDailyQuiz,
  fetchDailyQuizByDate,
} from "../../redux/slices/dailyQuizSlice";
import { fetchQuestions } from "../../redux/slices/questionSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { useToast } from "../../components/ui/use-toast";
import { MultilingualInput, emptyMultilingual } from "../../components/common/MultilingualInput";
import { Plus, Trash2, Search, GripVertical } from "lucide-react";
import {
  questionListClass,
  questionListClassTall,
  questionRowHover,
} from "../../lib/quizStyles";

const DailyQuizDialog = ({ open, onOpenChange, dailyQuiz }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { loading } = useSelector((state) => state.dailyQuiz);
  const { questions: availableQuestions } = useSelector(
    (state) => state.question
  );

  const [activeTab, setActiveTab] = useState("details");
  const [formData, setFormData] = useState({
    active_date: "",
    title: emptyMultilingual(),
    description: emptyMultilingual(),
    total_time_limit_minutes: "",
    randomize_options: true,
    show_explanations_immediately: true,
    is_active: true,
  });

  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionSearch, setQuestionSearch] = useState("");

  useEffect(() => {
    if (open) {
      dispatch(fetchQuestions({ limit: 100 }));
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (dailyQuiz) {
      setFormData({
        active_date: dailyQuiz.active_date || "",
        title: dailyQuiz.title || emptyMultilingual(),
        description: dailyQuiz.description || emptyMultilingual(),
        total_time_limit_minutes: dailyQuiz.total_time_limit_minutes || "",
        randomize_options: dailyQuiz.randomize_options !== false,
        show_explanations_immediately:
          dailyQuiz.show_explanations_immediately !== false,
        is_active: dailyQuiz.is_active !== false,
      });

      // Load questions if editing (include_questions required for attached list)
      if (dailyQuiz.id) {
        dispatch(fetchDailyQuizByDate(dailyQuiz.active_date)).then((result) => {
          if (result.meta?.requestStatus !== "fulfilled") return;
          const questions = result.payload?.questions;
          if (Array.isArray(questions)) {
            setSelectedQuestions(
              questions.map((q) => {
                const pivot = q.DailyQuizQuestion || q.daily_quiz_question || {};
                return {
                  question_id: q.id,
                  order_number: pivot.order_number || 0,
                  marks_override: pivot.marks_override || null,
                  time_override_seconds: pivot.time_override_seconds || null,
                  ...q,
                };
              })
            );
          }
        });
      }
    } else {
      // Reset form for new quiz
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split("T")[0];

      setFormData({
        active_date: tomorrowStr,
        title: emptyMultilingual(),
        description: emptyMultilingual(),
        total_time_limit_minutes: "",
        randomize_options: true,
        show_explanations_immediately: true,
        is_active: true,
      });
      setSelectedQuestions([]);
    }
  }, [dailyQuiz, dispatch]);

  const handleSubmit = async () => {
    // Validate
    if (!formData.active_date) {
      toast({
        title: t("error"),
        description: t("dateRequired"),
        variant: "destructive",
      });
      return;
    }

    if (!formData.title.en) {
      toast({
        title: t("error"),
        description: t("titleRequired"),
        variant: "destructive",
      });
      return;
    }

    if (selectedQuestions.length === 0) {
      toast({
        title: t("error"),
        description: t("questionsRequired"),
        variant: "destructive",
      });
      return;
    }

    try {
      const payload = {
        active_date: formData.active_date,
        title: formData.title,
        description: formData.description,
        total_time_limit_minutes: formData.total_time_limit_minutes
          ? parseInt(formData.total_time_limit_minutes)
          : null,
        randomize_options: formData.randomize_options,
        show_explanations_immediately: formData.show_explanations_immediately,
        questions: selectedQuestions.map((q, index) => ({
          question_id: q.question_id || q.id,
          order_number: index + 1,
          marks_override: q.marks_override || null,
          time_override_seconds: q.time_override_seconds || null,
        })),
      };

      if (dailyQuiz?.id) {
        await dispatch(
          updateDailyQuiz({ id: dailyQuiz.id, data: payload })
        ).unwrap();
        toast({
          title: t("success"),
          description: t("dailyQuizUpdated"),
        });
      } else {
        await dispatch(scheduleDailyQuiz(payload)).unwrap();
        toast({
          title: t("success"),
          description: t("dailyQuizScheduled"),
        });
      }

      onOpenChange(false);
    } catch (error) {
      toast({
        title: t("error"),
        description: error.message || error,
        variant: "destructive",
      });
    }
  };

  const handleAddQuestion = (question) => {
    if (!selectedQuestions.find((q) => q.id === question.id)) {
      setSelectedQuestions([
        ...selectedQuestions,
        { ...question, question_id: question.id },
      ]);
    }
  };

  const handleRemoveQuestion = (questionId) => {
    setSelectedQuestions(
      selectedQuestions.filter((q) => (q.id || q.question_id) !== questionId)
    );
  };

  const handleUpdateQuestionOverride = (questionId, field, value) => {
    setSelectedQuestions(
      selectedQuestions.map((q) =>
        q.id === questionId || q.question_id === questionId
          ? { ...q, [field]: value === "" ? null : parseInt(value) || null }
          : q
      )
    );
  };

  const filteredQuestions = availableQuestions.filter((question) => {
    const matchesSearch =
      !questionSearch ||
      question.question_text?.en
        ?.toLowerCase()
        .includes(questionSearch.toLowerCase()) ||
      question.question_text?.ar
        ?.toLowerCase()
        .includes(questionSearch.toLowerCase());

    const notSelected = !selectedQuestions.some(
      (q) => q.id === question.id || q.question_id === question.id
    );

    return matchesSearch && notSelected && question.is_active;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {dailyQuiz ? t("editDailyQuiz") : t("scheduleDailyQuiz")}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">{t("details")}</TabsTrigger>
            <TabsTrigger value="questions">{t("questions")}</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="active_date">{t("quizDate")} *</Label>
              <Input
                id="active_date"
                type="date"
                value={formData.active_date}
                onChange={(e) =>
                  setFormData({ ...formData, active_date: e.target.value })
                }
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label>{t("title")} *</Label>
              <MultilingualInput
                value={formData.title}
                onChange={(title) => setFormData({ ...formData, title })}
                label={t("title")}
                placeholder={t("enterTitle")}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{t("description")}</Label>
              <MultilingualInput
                value={formData.description}
                onChange={(description) =>
                  setFormData({ ...formData, description })
                }
                label={t("description")}
                placeholder={t("enterDescription")}
                multiline
                rows={3}
              />
            </div>

            {/* Time Limit */}
            <div className="space-y-2">
              <Label htmlFor="time_limit">{t("timeLimitMinutes")}</Label>
              <Input
                id="time_limit"
                type="number"
                value={formData.total_time_limit_minutes}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    total_time_limit_minutes: e.target.value,
                  })
                }
                placeholder={t("enterTimeLimit")}
              />
            </div>

            {/* Settings */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>{t("randomizeOptions")}</Label>
                <Switch
                  checked={formData.randomize_options}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, randomize_options: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>{t("showExplanationsImmediately")}</Label>
                <Switch
                  checked={formData.show_explanations_immediately}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      show_explanations_immediately: checked,
                    })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>{t("active")}</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="questions" className="space-y-4">
            {/* Selected Questions */}
            <div className="space-y-2">
              <Label>
                {t("selectedQuestions")} ({selectedQuestions.length})
              </Label>
              <div className={questionListClass}>
                {selectedQuestions.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    {t("noQuestionsSelected")}
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {selectedQuestions.map((question, index) => (
                      <div
                        key={question.id || question.question_id}
                        className="p-3 flex items-center gap-3"
                      >
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {index + 1}.
                        </span>
                        <span className="flex-1 text-sm">
                          {question.question_text?.en || t("untitled")}
                        </span>
                        <Badge variant="secondary">
                          {question.question_type}
                        </Badge>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <Label className="text-xs text-muted-foreground">
                              {t("points")}:
                            </Label>
                            <Input
                              type="number"
                              min="1"
                              max="100"
                              value={
                                question.marks_override ||
                                question.default_marks ||
                                ""
                              }
                              onChange={(e) =>
                                handleUpdateQuestionOverride(
                                  question.id || question.question_id,
                                  "marks_override",
                                  e.target.value
                                )
                              }
                              className="w-16 h-8 text-xs"
                              placeholder={question.default_marks || "5"}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleRemoveQuestion(
                                question.id || question.question_id
                              )
                            }
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Available Questions */}
            <div className="space-y-2">
              <Label>{t("availableQuestions")}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("searchQuestions")}
                  value={questionSearch}
                  onChange={(e) => setQuestionSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className={questionListClassTall}>
                {filteredQuestions.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    {t("noQuestionsAvailable")}
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredQuestions.map((question) => (
                      <div
                        key={question.id}
                        className={`p-3 flex items-center gap-3 ${questionRowHover}`}
                      >
                        <span className="flex-1 text-sm">
                          {question.question_text?.en || t("untitled")}
                        </span>
                        <Badge variant="secondary">
                          {question.question_type}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAddQuestion(question)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? t("saving") : dailyQuiz ? t("update") : t("schedule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DailyQuizDialog;
